import { useState } from 'react';
import {
  usePreisToolDispositionSettings,
  useSaveDispositionSettings,
} from '@/api/preis-tool/preis-tool.hooks';
import type { PreisToolDispositionSettings } from '@/api/preis-tool/preis-tool.types';
import {
  DsAlert,
  DsButton,
  DsInput,
  DsModal,
  DsSegmented,
} from '@/modules/internal-v2/ds';
import {
  DISPOSITION_DAY_FIELDS,
  DISPOSITION_SETTINGS_LIMITS,
  type DispositionSettingsForm,
  fmtTargetMargin,
  fromDispositionSettingsForm,
  parseNewTargetMargin,
  toDispositionSettingsForm,
} from '../disposition-settings';
import { fmtDateTime } from '../helpers';

interface DispositionSettingsDialogProps {
  /** Whether the user may change the settings (`PREIS_TOOL_MANAGE`). */
  canEdit: boolean;
  onClose: () => void;
}

const TITLE = 'Einstellungen der Einkaufshilfe';
const DESCRIPTION = 'Gilt für alle Nutzer und alle Artikel. Die Formeln bleiben gleich, nur ihre Werte sind einstellbar.';

/**
 * The values the purchasing aid computes with (EOS-62), shared by everyone:
 * periods, safety time, the outlier share and the target margins. Read-only
 * without `PREIS_TOOL_MANAGE`.
 */
export function DispositionSettingsDialog({ canEdit, onClose }: DispositionSettingsDialogProps) {
  const { data, isPending, isError } = usePreisToolDispositionSettings();

  if (isPending || isError || !data) {
    return (
      <DsModal
        open
        onClose={onClose}
        title={TITLE}
        description={DESCRIPTION}
        footer={<DsButton onClick={onClose}>Schließen</DsButton>}
      >
        <div className="pt-fix-empty">
          {isPending ? 'Lade Einstellungen…' : 'Fehler beim Laden der Einstellungen.'}
        </div>
      </DsModal>
    );
  }
  // A newer stored version (saved by someone else meanwhile) starts the form anew.
  return <SettingsForm key={data.updatedAt ?? 'defaults'} settings={data} canEdit={canEdit} onClose={onClose} />;
}

function SettingsForm({
  settings,
  canEdit,
  onClose,
}: {
  settings: PreisToolDispositionSettings;
  canEdit: boolean;
  onClose: () => void;
}) {
  const save = useSaveDispositionSettings();
  const [form, setForm] = useState(() => toDispositionSettingsForm(settings));
  const [newMargin, setNewMargin] = useState('');
  const [error, setError] = useState<string | null>(null);

  const set = <K extends keyof DispositionSettingsForm>(key: K, value: DispositionSettingsForm[K]) =>
    setForm((f) => ({ ...f, [key]: value }));
  const dirty = JSON.stringify(form) !== JSON.stringify(toDispositionSettingsForm(settings));
  const isDefault = JSON.stringify(form) === JSON.stringify(toDispositionSettingsForm(settings.defaults));
  const maxMargins = DISPOSITION_SETTINGS_LIMITS.targetMarginCount.max;

  const addMargin = () => {
    const result = parseNewTargetMargin(newMargin, form.targetMargins);
    if ('error' in result) return setError(result.error);
    setError(null);
    set('targetMargins', [...form.targetMargins, result.share].sort((a, b) => a - b));
    setNewMargin('');
  };

  const removeMargin = (margin: number) =>
    setForm((f) => ({
      ...f,
      targetMargins: f.targetMargins.filter((m) => m !== margin),
      defaultTargetMargin: f.defaultTargetMargin === margin ? null : f.defaultTargetMargin,
    }));

  const resetToDefaults = () => {
    setForm(toDispositionSettingsForm(settings.defaults));
    setNewMargin('');
    setError(null);
  };

  const submit = () => {
    const result = fromDispositionSettingsForm(form);
    if ('error' in result) return setError(result.error);
    setError(null);
    save.mutate(result.values, { onSuccess: onClose });
  };

  const saveError = save.isError
    ? save.error.response?.status === 403
      ? 'Keine Berechtigung, die Einstellungen zu ändern.'
      : 'Die Einstellungen konnten nicht gespeichert werden.'
    : null;
  const shownError = error ?? saveError;

  const status = settings.updatedAt
    ? `Zuletzt geändert am ${fmtDateTime(settings.updatedAt)} von ${settings.updatedByEmail ?? 'unbekannt'}`
    : 'Es gelten die Standardwerte.';

  return (
    <DsModal
      open
      onClose={onClose}
      title={TITLE}
      description={DESCRIPTION}
      footer={
        canEdit ? (
          <>
            <DsButton variant="ghost" onClick={onClose} disabled={save.isPending}>
              Abbrechen
            </DsButton>
            <DsButton variant="primary" onClick={submit} disabled={save.isPending || !dirty}>
              {save.isPending ? 'Speichern…' : 'Speichern'}
            </DsButton>
          </>
        ) : (
          <DsButton onClick={onClose}>Schließen</DsButton>
        )
      }
    >
      <div className="pt-form">
        {shownError && <DsAlert severity="error">{shownError}</DsAlert>}
        {!canEdit && (
          <DsAlert severity="info">
            Nur lesend – ändern dürfen Nutzer mit der Berechtigung „Preis-Tool: Einkaufsdaten pflegen“.
          </DsAlert>
        )}

        <div className="pt-dsettings-grid">
          {DISPOSITION_DAY_FIELDS.map((field) => (
            <SettingRow
              key={field.key}
              id={`pt-dsettings-${field.key}`}
              label={field.label}
              hint={field.hint}
              unit="Tage"
              value={form[field.key]}
              onChange={(value) => set(field.key, value)}
              canEdit={canEdit}
              inputMode="numeric"
            />
          ))}
          <SettingRow
            id="pt-dsettings-outlierShare"
            label="Warnung, wenn ein Auftrag mindestens"
            hint="Hinweis „verzerrter Absatz“ über den Kennzahlen"
            unit="% der Menge hat"
            value={form.outlierSharePct}
            onChange={(value) => set('outlierSharePct', value)}
            canEdit={canEdit}
            inputMode="decimal"
          />
        </div>

        <div className="pt-field">
          <span className="pt-label">Zielmargen zur Auswahl</span>
          <div className="pt-dsettings-margins">
            {form.targetMargins.map((margin) => (
              <span key={margin} className="ds-chip">
                {fmtTargetMargin(margin)}
                {canEdit && (
                  <button
                    type="button"
                    className="pt-dsettings-chip-x"
                    onClick={() => removeMargin(margin)}
                    disabled={form.targetMargins.length <= 1}
                    title={form.targetMargins.length <= 1 ? 'Mindestens eine Zielmarge bleibt' : 'Entfernen'}
                    aria-label={`Zielmarge ${fmtTargetMargin(margin)} entfernen`}
                  >
                    ×
                  </button>
                )}
              </span>
            ))}
            {canEdit && form.targetMargins.length < maxMargins && (
              <span className="pt-dsettings-add">
                <DsInput
                  mono
                  value={newMargin}
                  onChange={(e) => setNewMargin(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') addMargin();
                  }}
                  placeholder="z. B. 2,5"
                  inputMode="decimal"
                  aria-label="Neue Zielmarge in Prozent"
                  style={{ width: 80 }}
                />
                <span className="unit">%</span>
                <DsButton size="small" onClick={addMargin} disabled={newMargin.trim() === ''}>
                  Hinzufügen
                </DsButton>
              </span>
            )}
          </div>
          <span className="pt-fix-hint">
            Die Auswahl über den Kennzahlen; das Kauflimit ist der Standard-VK ÷ (1 + Zielmarge). Höchstens{' '}
            {maxMargins} Werte.
          </span>
        </div>

        <div className="pt-field">
          <span className="pt-label">Standard-Zielmarge</span>
          {canEdit ? (
            <DsSegmented
              ariaLabel="Standard-Zielmarge"
              size="small"
              options={form.targetMargins.map((m) => ({ value: m, label: fmtTargetMargin(m) }))}
              value={form.defaultTargetMargin}
              onChange={(m) => set('defaultTargetMargin', m)}
            />
          ) : (
            <span className="pt-dsettings-value">{fmtTargetMargin(settings.defaultTargetMargin)}</span>
          )}
          <span className="pt-fix-hint">
            {form.defaultTargetMargin == null
              ? 'Bitte eine der Zielmargen als Standard wählen.'
              : 'Gilt in der Einkaufshilfe, solange keine andere Zielmarge gewählt ist.'}
          </span>
        </div>

        <div className="pt-dsettings-status">
          <span>
            {status}
            {dirty && ' · Änderungen gelten erst nach „Speichern“.'}
          </span>
          {canEdit && (
            <DsButton size="small" variant="ghost" onClick={resetToDefaults} disabled={isDefault}>
              Auf Standardwerte zurücksetzen
            </DsButton>
          )}
        </div>
      </div>
    </DsModal>
  );
}

/** One value: label with explanation, the input (or the value), the unit. */
function SettingRow({
  id,
  label,
  hint,
  unit,
  value,
  onChange,
  canEdit,
  inputMode,
}: {
  id: string;
  label: string;
  hint: string;
  unit: string;
  value: string;
  onChange: (value: string) => void;
  canEdit: boolean;
  inputMode: 'numeric' | 'decimal';
}) {
  return (
    <>
      <label className="label" htmlFor={id}>
        {label}
        <span className="hint">{hint}</span>
      </label>
      {canEdit ? (
        <DsInput id={id} mono value={value} onChange={(e) => onChange(e.target.value)} inputMode={inputMode} />
      ) : (
        <span id={id} className="pt-dsettings-value">
          {value}
        </span>
      )}
      <span className="unit">{unit}</span>
    </>
  );
}
